import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

async function retryRequest<T>(fn: () => Promise<T>, retries = 0): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries < MAX_RETRIES && (error.message?.includes('Failed to fetch') || error.name === 'FunctionsFetchError')) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retries)));
      return retryRequest(fn, retries + 1);
    }
    throw error;
  }
}

async function createStripeCheckoutSession(plan: { stripe_price_id?: string }): Promise<string> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please sign in to continue');
      throw new Error('User not authenticated');
    }

    if (!plan.stripe_price_id) {
      throw new Error('Invalid subscription plan');
    }

    // Create checkout session with the Stripe price ID
    const { data, error } = await retryRequest(() =>
      supabase.functions.invoke('create-stripe-checkout', {
        body: { 
          priceId: plan.stripe_price_id,
          userId: user.id,
          successUrl: `${window.location.origin}/prestige-club?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/subscription`
        }
      })
    );

    if (error) throw error;
    if (!data?.url) throw new Error('No checkout URL returned');

    return data.url;
  } catch (error) {
    console.error('Stripe checkout error:', error);
    toast.error('Failed to start checkout process. Please try again.');
    throw error;
  }
}

export const createStripePortalSession = async () => {
  try {
    // Get user's subscription data
    const { data: subscription, error: subError } = await supabase
      .from('user_subscriptions')
      .select('stripe_customer_id')
      .single();

    if (subError) throw subError;
    if (!subscription?.stripe_customer_id) {
      throw new Error('No customer ID found');
    }

    // Create portal session
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: {
        customerId: subscription.stripe_customer_id,
        returnUrl: `${window.location.origin}/settings`
      }
    });

    if (error) throw error;
    if (!data?.url) throw new Error('No portal URL returned');
    
    return data.url;
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};